import { Loader2, Sparkles } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useMemo, useRef } from "react";
import { useHref } from "react-router-dom";

import { createAskMarkdown } from "./askMarkdown";
import type { AskClaude, AskTarget } from "./useAskClaude";

// Eksempler i den tomme chatten. Klikk sender spørsmålet med en gang.
const EXAMPLES = [
  "Hvilke oppgaver har jeg som er forfalt?",
  "Hvilke kunder har vi ikke snakket med på tre måneder?",
  "Hva var den siste e-postutsendingen vår?",
];

// Eksempler når samtalen gjelder én post («Spør Claude» på posten).
const CONTEXT_EXAMPLES: Record<AskTarget["type"], string[]> = {
  customer: [
    "Oppsummer kunden før et møte",
    "Hva har vi gjort for kunden de siste tre månedene?",
    "Hvilke oppgaver er åpne eller forfalt her?",
  ],
  project: [
    "Hva er status i prosjektet?",
    "Hva gjenstår, og hva er forfalt?",
    "Hvem jobber med hva her?",
  ],
  deal: [
    "Oppsummer muligheten",
    "Hva er neste steg, og er det forfalt?",
    "Hva har skjedd siden sist?",
  ],
};

// Samtalen i ask-modus, mellom topplinjen og skrivefeltet. Fyller høyden og
// scroller selv.
export const AskPanel = ({
  ask,
  onNavigate,
  onExample,
}: {
  ask: AskClaude;
  // Klikk på en mimin://-lenke i svaret: naviger i appen (og lukk paletten).
  onNavigate: (path: string) => void;
  // Klikk på et eksempelspørsmål i den tomme chatten.
  onExample: (text: string) => void;
}) => {
  const translate = useTranslate();
  const { messages, status, statusText, error, sources, context } = ask;
  const examples = context ? CONTEXT_EXAMPLES[context.type] : EXAMPLES;
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  // Routerens base («#/» med HashRouter), så lenkene også virker med
  // midtklikk og «åpne i ny fane».
  const root = useHref("/");
  const render = useMemo(
    () =>
      createAskMarkdown((path) =>
        root.endsWith("/") ? `${root}${path.slice(1)}` : `${root}${path}`,
      ),
    [root],
  );

  // Følg med nedover mens svaret strømmer, med mindre brukeren har scrollet
  // opp for å lese.
  const lastContent = messages[messages.length - 1]?.content;
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [lastContent, statusText, error, messages.length]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  const onClick = (event: React.MouseEvent) => {
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(
      "a[data-mimin-path]",
    );
    if (
      !link ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate(link.dataset.miminPath!);
  };

  if (messages.length === 0) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-6 py-8 text-center">
        {/* Ingen glød eller gradient bak (kap. 16.9 og AK45): tom tilstand
            på flat bakgrunn. */}
        <div className="relative grid size-11 place-items-center rounded-xl bg-primary/12 text-primary">
          <Sparkles className="size-5" />
        </div>
        <h2 className="relative mt-4 text-xl font-semibold tracking-tight text-balance">
          {context
            ? translate("crm.command.ask.about_title", {
                name: context.name,
                _: "Spør om %{name}",
              })
            : translate("crm.command.ask.empty_title", {
                _: "Spør om hva som helst i Mimin",
              })}
        </h2>
        <p className="relative mt-1.5 max-w-sm text-sm text-pretty text-muted-foreground">
          {context
            ? translate("crm.command.ask.about_empty", {
                _: "Claude henter posten og det som hører til, og viser kildene under svaret. Claude leser, men endrer ingenting.",
              })
            : translate("crm.command.ask.empty", {
                _: "Kunder, oppgaver, prosjekter, e-post og notater. Claude leser, men endrer ingenting.",
              })}
        </p>
        <div className="relative mt-6 flex w-full max-w-md flex-col gap-1.5">
          {examples.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => onExample(example)}
              className="rounded-xl border border-border/70 bg-background/50 px-3.5 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:border-border hover:bg-accent hover:text-foreground"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const lastIndex = messages.length - 1;

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="min-h-0 flex-1 overflow-y-auto"
    >
      <ol
        className="flex flex-col gap-5 px-5 py-5 text-sm"
        aria-live="polite"
        aria-busy={status === "streaming"}
      >
        {messages.map((message, index) => {
          if (message.role === "user") {
            return (
              <li
                key={index}
                className="max-w-[85%] self-end rounded-2xl rounded-br-md bg-muted px-3.5 py-2 whitespace-pre-wrap"
              >
                {message.content}
              </li>
            );
          }
          const isLast = index === lastIndex;
          const streaming = isLast && status === "streaming";
          const empty = message.content.trim().length === 0;
          return (
            <li key={index} className="flex flex-col gap-1.5">
              {streaming && statusText && empty && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" />
                  {statusText}
                </div>
              )}
              {(!empty || streaming) && (
                <div
                  onClick={onClick}
                  className={ANSWER_CLASSES}
                  dangerouslySetInnerHTML={{
                    __html: render(message.content, {
                      cursor: streaming,
                      sources,
                    }),
                  }}
                />
              )}
              {empty &&
                !streaming &&
                !message.aborted &&
                !(isLast && error) && (
                  <p className="text-muted-foreground">
                    {translate("crm.command.ask.no_answer", {
                      _: "Fant ikke noe svar på det.",
                    })}
                  </p>
                )}
              {message.aborted && (
                <p className="text-xs text-muted-foreground">
                  {translate("crm.command.ask.aborted", { _: "Avbrutt" })}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {status === "error" && error && (
        <div
          role="alert"
          className="mx-4 mb-4 flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={ask.retry}
            className="shrink-0 rounded px-2 py-1 text-xs font-medium hover:bg-accent"
          >
            {translate("crm.command.ask.retry", { _: "Prøv igjen" })}
          </button>
        </div>
      )}
    </div>
  );
};

const ANSWER_CLASSES = [
  "leading-6",
  "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
  "[&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:my-2 [&_li]:my-0.5",
  "[&_strong]:font-semibold",
  "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:no-underline",
  "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:font-mono [&_code]:text-xs",
  "[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:my-2 [&_h2]:my-2 [&_h3]:my-2",
  "[&_table]:my-2 [&_table]:w-full [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:px-2 [&_td]:py-1",
  // Pulserende markør på slutten av svaret mens det strømmer.
  "[&_.ask-cursor]:ml-0.5 [&_.ask-cursor]:inline-block [&_.ask-cursor]:h-4 [&_.ask-cursor]:w-1.5 [&_.ask-cursor]:translate-y-0.5 [&_.ask-cursor]:animate-pulse [&_.ask-cursor]:rounded-sm [&_.ask-cursor]:bg-foreground/60",
].join(" ");
